import React, { useState } from 'react';
import { Anchor, Box, Button, DataTable, Heading, Layer, Text } from 'grommet';
import { Close } from 'grommet-icons';
import PropTypes from 'prop-types';
import JobService from '../../api/job-service';
import CandidacyService from '../../api/candidacy-service';

export const GenerateMatchesButton = ({ job }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <Box>
            <Button
                primary
                size="large"
                label="Generate matches"
                onClick={() => setShowModal(true)}
            />
            {showModal && (
                <GenerateMatchesModal job={job} setShowModal={setShowModal} />
            )}
        </Box>
    );
};

const GenerateMatchesModal = ({ job, setShowModal }) => {
    const [buttonBusy, setButtonBusy] = useState();
    const [matchSuggestions, setMatchSuggestions] = useState();

    const generateMatches = async () => {
        try {
            setButtonBusy(true);
            const matches = await new JobService().generateMatches(job.id);
            setMatchSuggestions(matches);
            setButtonBusy(false);
        } catch (error) {
            setButtonBusy(false);
            console.log(error);
        }
    };

    return (
        <Layer
            onClickOutside={() => setShowModal(false)}
            onEsc={() => setShowModal(false)}
            position="center">
            <Box
                background="white"
                pad="medium"
                round="6px"
                border={{ color: 'enrich-green-light', size: '3px' }}
                alignSelf="center"
                gap="30px">
                <Box direction="row" gap="20px" justify="between">
                    <Heading level="3" margin={{ vertical: 'none' }}>
                        Generate job match suggestions
                    </Heading>
                    <Box align="end">
                        <Close
                            onClick={() => {
                                setShowModal(false);
                            }}
                            cursor="pointer"
                        />
                    </Box>
                </Box>
                <Box>
                    <Box
                        width={{ min: 'medium' }}
                        align="center"
                        round="6px"
                        background="white">
                        <Text>
                            This will suggest matches based on the job&apos;s
                            weight fields. Weights with a value of 100 are
                            considered requirements and will not be displayed in
                            the score breakdown.
                        </Text>
                    </Box>
                </Box>

                {matchSuggestions && (
                    <Box overflow="scroll">
                        <MatchSuggestions
                            matches={matchSuggestions}
                            job={job}
                        />
                    </Box>
                )}

                <Box align="center">
                    <Button
                        primary
                        busy={buttonBusy}
                        label="Suggest matches"
                        style={{ width: '220px', height: '50px' }}
                        onClick={generateMatches}
                    />
                </Box>
            </Box>
        </Layer>
    );
};

const MatchSuggestions = ({ matches, job }) => {
    const dataColumns = [
        {
            header: 'Name',
            render: (match) => {
                return (
                    <Anchor
                        key={match.id}
                        href={`/contacts/${match.id}`}
                        label={match.name}
                    />
                );
            },
            property: 'name',
        },
        {
            header: 'Match Score',
            property: 'score',
        },
        {
            header: 'Score Breakdown',
            property: 'scoreLog',
        },
        {
            header: 'Action',
            property: 'id',
            render: (match) => {
                const [buttonBusy, setButtonBusy] = useState();
                const [buttonDisabled, setButtonDisabled] = useState();

                return (
                    <Box key={match.id}>
                        <Button
                            busy={buttonBusy}
                            label={buttonDisabled ? 'Created' : 'Create'}
                            disabled={buttonDisabled}
                            primary
                            onClick={async () => {
                                setButtonBusy(true);
                                await new CandidacyService().createCandidacy({
                                    contact: match.id,
                                    job: job.id,
                                    jsonData: {
                                        score: match.score,
                                        scoreLog: match.scoreLog,
                                    },
                                });
                                setButtonDisabled(true);
                                setButtonBusy(false);
                            }}
                        />
                    </Box>
                );
            },
        },
    ];

    return (
        <Box>
            <DataTable primaryKey="id" data={matches} columns={dataColumns} />
        </Box>
    );
};

GenerateMatchesButton.propTypes = {
    job: PropTypes.object,
};

GenerateMatchesModal.propTypes = {
    job: PropTypes.object,
    setShowModal: PropTypes.func,
};

MatchSuggestions.propTypes = {
    job: PropTypes.object,
    matches: PropTypes.array,
};
